import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { UserPlus, Loader2, CheckCircle, AlertTriangle } from 'lucide-react'
import { api } from '../api/client'

export default function AcceptInvite() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    try {
      await api.post('/auth/accept-invite', {
        token,
        password,
        full_name: fullName || undefined,
      })
      setSuccess(true)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to activate account')
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-background py-12 px-4">
        <div className="max-w-md w-full text-center space-y-4 bg-theme-surface p-8 rounded-lg shadow border border-theme-border">
          <AlertTriangle className="mx-auto text-yellow-500" size={48} />
          <h2 className="text-lg font-semibold text-theme-text-primary">Invalid Invitation</h2>
          <p className="text-theme-text-secondary text-sm">This link is missing an invitation token.</p>
          <Link to="/login" className="text-theme-primary hover:text-theme-primary-hover text-sm font-medium">
            Go to login
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-background py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-theme-text-primary">Forge-Quote</h1>
          <h2 className="mt-2 text-xl text-theme-text-secondary">Accept your invitation</h2>
        </div>

        <div className="bg-theme-surface p-8 rounded-lg shadow border border-theme-border">
          {success ? (
            <div className="text-center space-y-4">
              <CheckCircle className="mx-auto text-green-500" size={48} />
              <h3 className="text-lg font-semibold text-theme-text-primary">Account activated!</h3>
              <p className="text-theme-text-secondary text-sm">
                Your account is ready. You can now sign in.
              </p>
              <Link
                to="/login"
                className="inline-block px-6 py-2 bg-theme-primary text-white rounded-md hover:bg-theme-primary-hover text-sm font-medium"
              >
                Sign In
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
              <p className="text-theme-text-secondary text-sm mb-4">
                Set up your account to get started.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-theme-text-primary">Full Name</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    placeholder="Optional"
                    className="mt-1 block w-full border border-theme-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-theme-primary focus:border-theme-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-theme-text-primary">Password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="mt-1 block w-full border border-theme-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-theme-primary focus:border-theme-primary"
                  />
                  <p className="text-xs text-theme-text-secondary mt-1">Minimum 8 characters</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-theme-text-primary">Confirm Password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    className="mt-1 block w-full border border-theme-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-theme-primary focus:border-theme-primary"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center items-center gap-2 py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white bg-theme-primary hover:bg-theme-primary-hover disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>
                      <UserPlus size={20} />
                      Activate Account
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
